<template>
  <vonap-fullscreen-dialog :title="dealFormTitle" @close="close">
    <template v-slot:toolbar-items>
      <v-btn title="Add new" @click.prevent="addNewClick" flat fab small v-if="!isReadOnly">
        <v-icon>add</v-icon>
      </v-btn>
      <kendo-dropdownlist
        title="Status"
        :data-source="statusDataSource"
        :data-text-field="'statusName'"
        :data-value-field="'statusId'"
        :disabled="!statusEnabled"
        v-model="dealModel.statusId"
        style="width: 100px"
      >
      </kendo-dropdownlist>
      <v-btn
        type="submit"
        flat
        v-if="!isReadOnly"
        @click="onSubmit"
        :disabled="saving"
        :loading="saving"
      >
        <v-icon>check</v-icon>
        Save</v-btn
      >
      <v-btn small icon color="primary" @click="closeAndRefresh" title="Close"
        ><v-icon>close</v-icon></v-btn
      >
    </template>
    <vonap-promised :promise="loadData">
      <v-container fluid grid-list-md text-xs-left class="px-0 py-0 mx-0 my-0" id="">
        <v-layout row wrap>
          <v-flex xs12>
            <div class="blue darken-2 layout row wrap white--text mx-auto">
              <v-flex d-flex xs6>
                <v-layout justify-start row>
                  <v-flex v-if="clients.length > 1">
                    <label>Client: </label> <span>{{ clientName }}</span>
                  </v-flex>
                </v-layout>
              </v-flex>
              <v-flex d-flex xs6>
                <div class="text-xs-right">
                  <span class="font-weight-thin">
                    {{ modified }}
                  </span>
                </div>
              </v-flex>
            </div>
          </v-flex>
          <v-flex xs12 style="margin-top: 15px">
            <v-form v-model="valid" v-kendo-validator @submit.prevent="() => {}">
              <deal-form-header
                :deal-id="dealId"
                :is-enabled="isEnabled"
                :dealModel="dealModel"
                :dealTypeId="dealTypeId"
                :marketId="currentMarketId"
                @productChange="productChange"
              />
            </v-form>
          </v-flex>
          <v-flex xs12 class="px-0 py-0 mx-0 my-0">
            <deal-order
              ref="order"
              :dealModel="dealModel"
              :order="dealModel.order"
              :tradeDate="dealModel.tradeDate"
              :default-deal-options="dealModel.defaultDealOptions"
              :is-read-only="isReadOnly"
              :editable="editable"
              :marketId="currentMarketId"
              @updateOrder="dealModel.order = $event"
              @clearDeals="cancelGridChanges"
              @showHideOrder="showHideOrder"
              :deal-type-id="currentDealTypeId"
            />
            <component
              ref="leg"
              :is="dealTypeName"
              :is-read-only="isReadOnly"
              :editable="editable"
              :dealModel="dealModel"
              @save-operation="onSubmit"
              :marketId="currentMarketId"
              :dealTypeId="currentDealTypeId"
              :hasOrders="orderVisible"
              :key="orderVisible"
            />
          </v-flex>
        </v-layout>
      </v-container>
    </vonap-promised>
  </vonap-fullscreen-dialog>
</template>

<script>
import Vue from 'vue'
import axios from 'axios'
import notification from '@/helpers/notification'
import VonapFullscreenDialog from '../../../components/VonapFullscreenDialog'
import VonapPromised from '../../../components/VonapPromised'
import {mapGetters} from 'vuex'
import DealFormHeader from '@/views/List/Deal/DealFormHeader'
import FWD from '@/views/List/Deal/FxDeal'
import FUT from '@/views/List/Deal/Future'
import DealOrder from './DealOrder'

const EventBus = new Vue()

export default {
  name: 'DealForm',
  components: {
    VonapFullscreenDialog,
    VonapPromised,
    DealFormHeader,
    FWD,
    OPT: FWD,
    FUT,
    DealOrder,
  },
  provide: {
    EventBus,
  },
  props: {
    dealId: {type: [Number, String], default: null},
    isReadOnly: {type: Boolean, default: true},
    modalVisible: Boolean,
    isDialog: {type: Boolean, default: true},
    defaultTradeDate: {type: String, default: null},
    dealTypeId: {type: Number, default: 1},
    marketId: {type: Number, default: 1},
  },
  data() {
    return {
      valid: false,
      formStatusId: 1,
      saving: false,
      statusDataSource: new kendo.data.DataSource({
        transport: {
          read: {
            type: 'GET',
            url: '/api/list/deal/status',
            dataType: 'json',
          },
        },
        schema: {
          model: {
            id: 'statusId',
            fields: {
              statusId: {type: 'number'},
              statusName: {type: 'string'},
            },
          },
        },
      }),
      dealModel: {
        description: 'loading...',
        order: null,
      },
      currentDealType: null,
      currentDealTypeId: 0,
      orderVisible: false,
    }
  },
  computed: {
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    ...mapGetters({currentRole: 'authentication/currentRole'}),
    ...mapGetters({clients: 'authentication/clients'}),

    currentMarketId() {
      return this.dealModel.marketId || this.marketId
    },
    hasOrders() {
      return this.dealModel && this.dealModel.order != null
    },
    dealFormTitle: function () {
      return this.isNew ? 'Create Deal' : this.dealModel.description + ' Id: ' + this.dealId
    },
    clientName: function () {
      return this.dealModel.clientName
    },
    modified: function () {
      return this.isNew
        ? 'Today'
        : 'Modified by: ' +
            this.dealModel.lastUser +
            ' on ' +
            kendo.toString(kendo.parseDate(this.dealModel.lastModified), 'yyyy-MM-dd HH:mm')
    },
    isNew: function () {
      return this.dealId == 0
    },
    isGuest() {
      return this.currentRole === 'Guest'
    },
    isAdmin() {
      return this.currentRole === 'Admin' || this.currentRole === 'SuperAdmin'
    },
    editable: function () {
      return !this.isGuest && this.formStatusId != 2
    },
    statusEnabled: function () {
      return !this.isReadOnly && (this.isAdmin || this.formStatusId == 1)
    },
    isEnabled: function () {
      return !this.isGuest && this.formStatusId == 1
    },
    loadData() {
      return this.getDeal(this.dealId, this.clientId, this.dealTypeId, this.marketId)
    },
    dealTypeName() {
      if (this.currentDealType) return this.currentDealType
      return this.dealModel.dealType
    },
  },
  methods: {
    close() {
      this.$emit('close')
    },
    closeAndRefresh() {
      this.$emit('close')
      this.$emit('close-and-refresh')
    },
    save(dealId) {
      this.$emit('save-event', dealId)
    },
    addNewClick() {
      this.$emit('create-new')
    },
    showValidationErrors(errors) {
      errors.forEach(function (error) {
        if (error) {
          notification(error, 'error')
        }
      })
    },
    getDeal: async function (dealId, clientId, dealTypeId, marketId) {
      const req = await axios
        .get('/api/list/deal/getDeal', {
          params: {
            dealId,
            clientId,
            dealTypeId,
            marketId,
            t: new Date().getTime(),
          },
        })
        .catch(() => notification('Error getting deal information', 'error'))
      let model = req.data[0]

      if (this.defaultTradeDate != null && dealId == 0) {
        model.tradeDate = this.defaultTradeDate
      }

      this.dealModel = model
      this.formStatusId = model.statusId
    },
    onSubmit: function () {
      let validator = this.kendoValidator
      if (validator.validate()) {
        this.updateDeal()
      } else {
        let errors = validator.errors()
        this.showValidationErrors(errors)
      }
    },
    updateDeal: async function () {
      // Prevent double-clicking the save button
      if (this.saving) {
        return
      }

      try {
        this.saving = true

        if (this.$refs.leg.isEmpty() && this.$refs.order.isEmpty()) {
          if (this.dealModel.dealId == 0) {
            notification('Cannot create a deal without leg information', 'error')
            return
          }
          if (!confirm('The deal will be deleted, do you want to continue?')) {
            this.cancelGridChanges()
            return
          }
        }

        const validation = await this.$refs.leg.validate()
        await this.$refs.order.sync()

        if (validation.length) {
          this.showValidationErrors(validation)
          return
        }

        const data = this.fixModelDates(this.dealModel)
        const res = await axios
          .put('/api/list/deal/updateDeal', data)
          .catch(() => notification('Some error ocurred', 'error'))

        if (res.data == 'DELETED') {
          this.closeAndRefresh()
        } else {
          let response = res.data
          this.dealId = response.dealId
          this.formStatusId = this.dealModel.statusId
          Object.assign(this.dealModel, response)
          this.save(response.dealId)
        }
      } finally {
        // Always re-enable the button, even if there was an error
        this.saving = false
      }
    },
    cancelGridChanges() {
      setTimeout(() => this.$refs.leg.cancelChanges(), 0)
      this.$refs.order.cancelGridChanges()
    },
    showHideOrder(value) {
      this.orderVisible = value
      this.cancelGridChanges()
      if (!value) {
        this.dealModel.order = null
      }
    },
    productChange(e) {
      this.currentDealType = e.dealType
      this.currentDealTypeId = e.dealTypeId
      this.dealModel.dealTypeId = e.dealTypeId
      this.dealModel.marketId = e.marketId
      this.dealModel.defaultDealOptions = e.defaultDealOptions
    },
    fixModelDates(dealModel) {
      function adjustDate(date) {
        const tzOffset = date.getTimezoneOffset() * 60000
        return new Date(date - tzOffset).toISOString().slice(0, 10)
      }

      const replacer = function (key, value) {
        if (this[key] == null) return value

        if (this[key] instanceof Date) {
          return adjustDate(this[key])
        }

        if (/Date/.test(key)) {
          return this[key].slice(0, 10)
        }
        return value
      }

      return JSON.parse(JSON.stringify(dealModel, replacer))
    },
  },
  watch: {
    'dealModel.order': {
      handler(value) {
        this.orderVisible = !!value
      },
      immediate: true,
    },
  },
}
</script>

<style>
.gridContainer {
  padding-top: 0px !important;
}
form .v-label,
form .k-input,
form span.k-widget.k-dropdown {
  font-size: 12px;
}
form textarea {
  display: flex;
  vertical-align: bottom;
  border: 1px;
  border-style: dotted;
  background: #f6f6f6;
}
</style>
